import Logger from "@/services/Logger"
import LocalAuthService from '@/services/LocalAuthService'
import ApiServiceBase from '@/services/ApiServiceBase'
import moment from 'moment'

const TokenValueKey = 'auth'

function parseJwt (token) {
  const base64Url = token.split('.')[1]
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      })
      .join('')
  )

  return JSON.parse(jsonPayload)
}

export const namespaced = true

export const state = {
  userData: null,
  accessToken: null
}

export const mutations = {
  SET_ACCESS_TOKEN (state, accessToken) {
    if (!accessToken?.length) {
      throw new Error("The 'accessToken' parameter cannot be null or empty.")
    }

    state.accessToken = accessToken
    localStorage.setItem(TokenValueKey, accessToken)
    ApiServiceBase.setAuthorizationHeader(accessToken)
  },
  SET_USER_DATA (state, userData) {
    state.userData = userData
  },
  CLEAR_ACCESS_TOKEN (state) {
    state.accessToken = null
    localStorage.removeItem(TokenValueKey)
    ApiServiceBase.clearAuthorizationHeader()
  },
  CLEAR_USER_DATA (state) {
    state.userData = null
  }
}

export const actions = {
  /**
   * Attempts to restore login state from browser localStorage.
   * @param commit
   * @returns {Promise}
   */
  tryRestoreLogin ({ commit }) {
    return new Promise(resolve => {
      const token = localStorage.getItem(TokenValueKey)
      if (!token || !token.length) {
        resolve(false)
        return
      }

      const tokenData = parseJwt(token)
      const tokenExp = moment.unix(tokenData.exp)

      if (tokenExp.isSameOrBefore(moment()) || !tokenData) {
        resolve(false)
      } else {
        commit('SET_ACCESS_TOKEN', token)

        const nameId =
          tokenData[
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
          ]

        LocalAuthService.getCurrentUser(nameId)
          .then(user => {
            commit('SET_USER_DATA', user)
            resolve(true)
          })
          // eslint-disable-next-line handle-callback-err
          .catch(err => {
            commit('CLEAR_ACCESS_TOKEN')
            commit('CLEAR_USER_DATA')
            Logger.error(err)
          })
      }
    })
  },
  /**
   * Performs a password login against a local (non-OIDC) user.
   * @returns {Promise}
   */
  verifyLogin () {
    return new Promise(resolve => {
      const token = localStorage.getItem(TokenValueKey)
      if (!token || !token.length) {
        resolve(false)
        return
      }

      const tokenData = parseJwt(token)
      const tokenExp = moment.unix(tokenData.exp)

      if (tokenExp.isSameOrBefore(moment()) || !tokenData) {
        resolve(false)
      } else {
        resolve(true)
      }
    })
  },
  /**
   * Attempts to send a one-type-password/verification code to the user's phone.
   * @param {string} emailAddress
   * @param {'sms'|'voice'} type
   */
  beginLogin (o, { emailAddress, type }) {
    return LocalAuthService.requestLogin(emailAddress, type)
  },
  /**
   * Performs a password login against a local (non-OIDC) user.
   * @param commit
   * @param userName
   * @param password
   * @returns {Promise<bool>}
   */
  completeLogin ({ commit }, { emailAddress, token }) {
    return LocalAuthService.completeLogin(emailAddress, token)
      .then(response => {
        if (!response.succeeded) {
          return
        }

        commit('SET_ACCESS_TOKEN', response.accessToken)

        const tokenData = parseJwt(response.accessToken)
        const nameId =
          tokenData[
            'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/nameidentifier'
          ]

        return LocalAuthService.getCurrentUser(nameId)
          .then(user => {
            commit('SET_USER_DATA', user)
            return {
              succeeded: true
            }
          })
          // eslint-disable-next-line handle-callback-err
          .catch(err => {
            commit('CLEAR_ACCESS_TOKEN')
            commit('CLEAR_USER_DATA')
            Logger.error(err)
          })
      })
      // eslint-disable-next-line handle-callback-err
      .catch(err => {
        commit('CLEAR_ACCESS_TOKEN')
        commit('CLEAR_USER_DATA')
        Logger.error(err)
      })
  },
  logout ({ commit }) {
    return new Promise(resolve => {
      commit('users/RESET_STATE', null, { root: true })
      commit('CLEAR_ACCESS_TOKEN')
      commit('CLEAR_USER_DATA')
      resolve()
    });
    // dispatch(
    //   'pushMessage',
    //   makeToast({
    //     type: 'success',
    //     text: 'You are now logged out.'
    //   }),
    //   { root: true }
    // )
  }
}

export const getters = {
  isLoggedIn (state) {
    return state.userData != null
  },
  userName (state) {
    return state.userData?.userName
  },
  userFullName (state) {
    if (state.userData == null) {
      return 'Not logged in.'
    }

    return `${state.userData.firstName} ${state.userData.lastName}`
  },
  isDeveloper (state) {
    return state.userData?.roles.indexOf('Developer') !== -1
  }
}
